import React from "react";
import { toast } from "sonner";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import userLogout from "../../utils/userLogout";

const PayNow = ({
  price,
  promoAmount,
  cartData,
  includeWallet,
  includePromo,
  suggestedBooks,
  promoInput,
  referralInput,
  gst,
  gatewayType,
}) => {
  const cart = useSelector((store) => store.cart.cart);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const placeFreeOrder = async () => {
    try {
      const authToken = sessionStorage.getItem("token");
      // alert(authToken+"");

      const payableAmount =
        Number(price || 0) +
        (includeWallet
          ? -Number(cartData?.wallet_balance || 0)
          : 0) +
        (includePromo
          ? -Number(promoAmount || 0)
          : 0);

      const totalSectionDivision = {
        [cart?.name]: cart?.sellingPrice,
        gst_percentage: `${gst * 100}`,
        gst_rate: cart?.price - cart?.sellingPrice,
        offer_discount: includePromo
          ? -Number(promoAmount || 0)
          : 0,
      };

      const params = new URLSearchParams({
        item_type: cart?.item || "course",
        order_status: "PAID",
        transaction_id: `FREE_${Date.now()}`,
        grand_total: Number(cart?.price || 0).toFixed(2),
        used_wallet: includeWallet
          ? Number(cartData?.wallet_balance || 0).toFixed(2)
          : "0.00",
        referral_code: referralInput || "",
        offer_code: promoInput || "",
        suggested_book: suggestedBooks?.length
          ? JSON.stringify(suggestedBooks)
          : "",
        total_section_division: JSON.stringify(
          totalSectionDivision
        ),
        transaction_amount: Math.max(
          payableAmount,
          0
        ).toFixed(2),
        test_package_id: "0",
        payment_plan_id: cart?.plan_id || "0",
        payment_gateway: gatewayType || "0",
        item_id: cart?.id || "",
        plan_validity: cart?.plan_validity || "",
      });

      const response = await fetch(
        `${process.env.REACT_APP_RANK_PLUS_PLACE_ORDER}?${params.toString()}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const json = await response.json();

      console.log("Place zero-payment order:", json);

      if (!response.ok) {
        throw new Error(
          `Place order failed: ${response.status}`
        );
      }

      if (json?.success || json?.status) {
        toast("Order placed successfully");

        navigate(
          "/"
        );

        return;
      }

      toast(
        json?.message ||
        "Unable to place order. Please try again."
      );
    } catch (error) {
      console.error("placeFreeOrder error:", error);
      toast("Unable to place order. Please try again.");
    }
  };

  const createOrder = async (retries = 6) => {
    try {
      const authToken = sessionStorage.getItem("token");
      const requestOptions = {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      };

      const payableAmount =
        Number(price || 0) +
        (includeWallet
          ? -Number(cartData?.wallet_balance || 0)
          : 0) +
        (includePromo
          ? -Number(promoAmount || 0)
          : 0);

      console.log("Payable amount:", payableAmount);

      if (payableAmount <= 0) {
        console.log("No gateway payment required:", payableAmount);
        placeFreeOrder();
        return;
      }

      if (gatewayType === '2') {
        // Razorpay API call
        const response = await fetch(
          process.env.REACT_APP_RANK_PLUS_CREATE_ORDER_RAZORPAY +
          `${(
            price +
            (includeWallet ? -parseFloat(cartData?.wallet_balance) : 0) +
            (includePromo ? -parseFloat(promoAmount) : 0)
          ).toFixed(2)}`,
          requestOptions
        );

        if (!response.ok) {
          if (response.status >= 400 && response.status < 500) {
            userLogout(navigate, dispatch, true);
          }
          if (response.status >= 500 && response.status < 600 && retries > 0) {
            console.warn(`Retrying... attempts left: ${retries}`);
            setTimeout(() => createOrder(retries - 1), 10000);
            return;
          } else {
            throw new Error(`HTTP error: ${response.status}`);
          }
        }

        const orderData = await response.json();
        console.log("Razorpay orderData", orderData);

        if (!orderData.id) {
          toast("Failed to create Razorpay order. Please try again.");
          return;
        }

        const loadScript = (src) => {
          return new Promise((resolve) => {
            const script = document.createElement("script");
            script.src = src;
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
          });
        };

        // Load Razorpay SDK
        const ok = await loadScript("https://checkout.razorpay.com/v1/checkout.js");

        if (!ok) {
          toast("Failed to load Razorpay SDK. Please try again.");
          return;
        }
        const userDetails = JSON.parse(sessionStorage.getItem("userDetail"));
        const options = {
          key: orderData.key_id,
          order_id: orderData.id,
          name: "RankPlus",
          description: "Order Payment",
          amount: orderData.amount,
          currency: "INR",
          handler: async function (response) {
            console.log("Razorpay payment response:", response);

            // Create cartbodyData with Razorpay-specific fields
            const cartbodyData = createCartBodyData(response.razorpay_payment_id, response.razorpay_order_id, response.razorpay_signature);

            sessionStorage.setItem("transaction_id", response.razorpay_payment_id);
            sessionStorage.setItem(response.razorpay_payment_id, JSON.stringify(cartbodyData));

            // Redirect to payment page
            navigate(`/payment?transaction_id=${response.razorpay_payment_id}`);
          },
          prefill: {
            name: userDetails?.userName,
            email: userDetails?.userEmail,
            contact: userDetails?.userMobile
          },
          theme: {
            color: "#3399cc"
          },
          modal: {
            ondismiss: function () {
              toast("Payment cancelled by user");
            }
          }
        };

        const rzp1 = new window.Razorpay(options);
        rzp1.open();

      } else {
        // Existing API call
        const response = await fetch(
          process.env.REACT_APP_RANK_PLUS_CREATE_ORDER +
          `${(
            price +
            (includeWallet ? -parseFloat(cartData?.wallet_balance) : 0) +
            (includePromo ? -parseFloat(promoAmount) : 0)
          ).toFixed(2)}`,
          requestOptions
        );

        if (!response.ok) {
          if (response.status >= 400 && response.status < 500) {
            userLogout(navigate, dispatch, true);
          }
          if (response.status >= 500 && response.status < 600 && retries > 0) {
            console.warn(`Retrying... attempts left: ${retries}`);
            setTimeout(() => createOrder(retries - 1), 10000);
            return;
          } else {
            throw new Error(`HTTP error: ${response.status}`);
          }
        }
        const json = await response.json();

        if (json?.success) {
          toast(json?.message);

          // Create cartbodyData with non-Razorpay transaction ID
          const cartbodyData = createCartBodyData(json?.data?.merchantTransactionId);

          sessionStorage.setItem(
            "transaction_id",
            json?.data?.merchantTransactionId
          );
          sessionStorage.setItem(
            json?.data?.merchantTransactionId,
            JSON.stringify(cartbodyData)
          );

          window.open(
            json?.data?.instrumentResponse?.redirectInfo?.url,
            "_blank"
          );
        } else {
          toast(
            "Some Problem has occured for the payment. Please Try After some time"
          );
        }
      }
    } catch (err) {
      console.error("Error createOrder", err);
    }
  };

  // Helper function to create cartbodyData
  const createCartBodyData = (transactionId, razorpayOrderId = null, razorpaySignature = null) => {
    const totalSectionDivision = {
      [cart?.name]: cart?.sellingPrice,
      gst_percentage: `${gst * 100}`,
      gst_rate: cart?.price - cart?.sellingPrice,
      offer_discount: includePromo
        ? -parseFloat(promoAmount).toFixed(2)
        : 0,
    };

    const baseCartData = {
      item_type: cart?.item,
      item_id: cart?.id,
      suggested_books: JSON.stringify(suggestedBooks),
      total_section_division: encodeURIComponent(
        JSON.stringify(totalSectionDivision)
      ),
      used_wallet: includeWallet
        ? -parseFloat(cartData?.wallet_balance).toFixed(2)
        : 0,
      offer_code: promoInput,
      referral_code: referralInput,
      grand_total: cart?.price,
      transaction_id: transactionId,
      transaction_amount: `${(
        price +
        (includeWallet ? -parseFloat(cartData?.wallet_balance) : 0) +
        (includePromo ? -parseFloat(promoAmount) : 0)
      ).toFixed(2)}`,
      test_package_id: "",
    };

    // Add Razorpay-specific fields if provided
    if (razorpayOrderId && razorpaySignature) {
      return {
        ...baseCartData,
        razorpay_order_id: razorpayOrderId,
        razorpay_signature: razorpaySignature,
        gatewayType: gatewayType
      };
    }

    return baseCartData;
  };

  function handleClick() {
    createOrder();
  }

  return (
    <div className="w-full flex justify-center items-center">
      <div className="text-white">h</div>
      <div>
        <button
          className="border-2 border-[#5A277C] px-4 py-1 rounded-2xl font-semibold hover:bg-[#5A277C] hover:text-white transition-all hover:-translate-y-1 shadow-md hover:shadow-lg"
          onClick={() => handleClick()}
        >
          {" "}
          Pay
        </button>
      </div>
    </div>
  );
};

export default PayNow;
